import { Currency } from '../../shared/lib/currency'
import { Product as LDProduct } from 'schema-dts'

export const LABEL_CUSTOM_MESSAGE = 'Custom message'
export const LABEL_NO_MESSAGE_REQUIRED = 'No message required'
export const CUSTOM_AMOUNT_LABEL_NAME = 'Custom'

export enum ValidationConstraints {
  minValue = 'minValue',
  maxValue = 'maxValue',
  maxDeliveryDateMonths = 'maxDeliveryDateMonths',
}

export interface IProductImage {
  url: string
  description?: string
  alt?: string | null
}

interface IEmptyImage {
  url: undefined
  description: undefined
}

export interface IProductCustomField {
  name: string
  value: string
}

export interface IProductAmount {
  label: string
  amount?: Currency
}

export enum ProductOptionType {
  DROPDOWN = 'DROPDOWN',
  // PRODUCT_LIST = 'PRODUCT_LIST', // unsupported
  // PRODUCT_LIST_WITH_IMAGES = 'PRODUCT_LIST_WITH_IMAGES', // unsupported
  RADIO_BUTTONS = 'RADIO_BUTTONS',
  RECTANGLES = 'RECTANGLES',
  SWATCH = 'SWATCH',
}

export interface IProductOptionValue {
  id: number
  label: string
}

export interface IProductOption {
  id: number
  type: ProductOptionType
  displayName: string
  values: IProductOptionValue[]
}

export enum ProductModifierType {
  TEXT = 'text',
  CHECKBOX = 'checkbox',
  DROPDOWN = 'dropdown',
}

export interface IProductModifier {
  id: number
  type: ProductModifierType
  displayName: string
  values: IProductOptionValue[]
}

export interface IProductVariant {
  id: number
  price: number
  image: IProductImage
  optionValues: {
    id: number
    optionId: number
  }[]
}

export type IProductConstraints = { [key in ValidationConstraints]: number }

export interface IGetProductResponseOption {
  id: number
  type: string
  displayName: string
  values: IProductOptionValue[]
}

interface IGetProductResponseModifier {
  id: number
  type: string
  displayName: string
  values: IProductOptionValue[]
}

export interface IGetProductResponse {
  id: number
  sku: string
  title: string
  pageTitle?: string
  description: string[]
  metaDescription: string
  images: IProductImage[]
  customFields: IProductCustomField[]
  constraints?: IProductConstraints
  variants: IProductVariant[]
  options?: IGetProductResponseOption[]
  modifiers?: IGetProductResponseModifier[]
}

interface IProduct {
  id: number
  sku: string
  title: string
  pageTitle?: string
  description: string[]
  metaDescription: string
  images: IProductImage[]
  amounts: IProductAmount[]
  personalisedMessages: string[]
  constraints: IProductConstraints
  variants: IProductVariant[]
}

export enum ThemesColours {
  BRAND = 'brand',
  SEASONAL = 'seasonal',
}

export class Product implements IProduct {
  public readonly id: number
  public readonly sku: string
  public readonly title: string
  public readonly pageTitle: string
  public readonly description: string[]
  public readonly metaDescription: string
  public readonly images: IProductImage[]
  public readonly amounts: IProductAmount[]
  public readonly personalisedMessages: string[]
  public readonly constraints: IProductConstraints
  public readonly variants: IProductVariant[]
  public readonly options: IProductOption[]
  public readonly modifiers: IProductModifier[]

  public constructor(init: IGetProductResponse) {
    this.id = init.id
    this.sku = init.sku
    this.title = init.title
    this.pageTitle = init.pageTitle
      ? `${init.pageTitle} | Westfield`
      : 'Westfield'
    this.description = init.description
    this.metaDescription = init.metaDescription
    this.images = init.images
    this.amounts = this.toAmounts(init.customFields)
    this.personalisedMessages = this.toPersonalisedMessages(init.customFields)
    // TO FIX: this is only non-nullable for
    // dgc products, but until now it's been
    // `init:constraints: any` so the code base
    // just assumed it would be here.
    this.constraints = init.constraints || ({} as IProductConstraints)
    this.variants = init.variants
    this.options = this.toProductOptions(init.options)
    this.modifiers = this.toProductModifiers(init.modifiers)
  }

  public get structuredData(): LDProduct {
    return {
      '@type': 'Product',
      name: this.title,
      description: this.description.join(' '),
      image: this.images.map(image => image.url),
      ...(this.constraints
        ? {
            offers: {
              '@type': 'AggregateOffer',
              lowPrice: this.constraints.minValue,
              highPrice: this.constraints.maxValue,
              priceCurrency: 'AUD',
            },
          }
        : {}),
    }
  }

  public get firstImage(): IProductImage | IEmptyImage {
    if (this.images[0]) {
      return this.images[0]
    } else {
      return { url: undefined, description: undefined }
    }
  }

  private toAmounts = (
    customFields: IProductCustomField[] = []
  ): IProductAmount[] => {
    const amounts = customFields
      .reduce(
        (acc: IProductAmount[], field) => {
          if (field.name === 'amount') {
            const amount: number = parseInt(field.value, 10)
            acc.push({
              label: !Number.isNaN(amount) ? `$${field.value}` : field.value,
              amount: !Number.isNaN(amount) ? new Currency(amount) : undefined,
            })
          }
          return acc
        },
        [{ label: CUSTOM_AMOUNT_LABEL_NAME }]
      )
      .sort((a: IProductAmount, b: IProductAmount): number => {
        if (a.amount && b.amount) {
          return a.amount.toNumber() - b.amount.toNumber()
        }

        return a.label > b.label ? 1 : -1
      })
    return amounts.length > 1 ? amounts : []
  }

  private toPersonalisedMessages = (
    customFields: IProductCustomField[] = []
  ): string[] => {
    const messages = customFields.reduce((acc: string[], field) => {
      if (field.name === 'personaliseMessage') {
        acc.push(field.value)
      }
      return acc
    }, [])
    return messages.length > 0
      ? [LABEL_NO_MESSAGE_REQUIRED, ...messages, LABEL_CUSTOM_MESSAGE]
      : []
  }

  private toSupportedProductOptionType = (
    type: string
  ): ProductOptionType | null => {
    switch (type) {
      case 'DROPDOWN':
        return ProductOptionType.DROPDOWN
      case 'RADIO_BUTTONS':
        return ProductOptionType.RADIO_BUTTONS
      case 'RECTANGLES':
        return ProductOptionType.RECTANGLES
      case 'SWATCH':
        return ProductOptionType.SWATCH
    }
    return null
  }

  private toProductOptions = (
    options?: IGetProductResponseOption[]
  ): IProductOption[] => {
    return (
      options
        ?.map(option => {
          const type = this.toSupportedProductOptionType(option.type)
          if (!type) {
            return null
          }
          return {
            id: option.id,
            type,
            displayName: option.displayName,
            values: option.values,
          }
        })
        .filter((o): o is IProductOption => o !== null) || []
    )
  }

  private toSupportedProductModifierType = (
    type: string
  ): ProductModifierType | null => {
    switch (type) {
      case 'text':
        return ProductModifierType.TEXT
      case 'checkbox':
        return ProductModifierType.CHECKBOX
      case 'dropdown':
        return ProductModifierType.DROPDOWN
    }
    return null
  }

  private toProductModifiers = (
    modifiers?: IGetProductResponseModifier[]
  ): IProductModifier[] => {
    return (
      modifiers
        ?.map(modifier => {
          const type = this.toSupportedProductModifierType(modifier.type)
          if (!type) {
            return null
          }
          return {
            id: modifier.id,
            type,
            displayName: modifier.displayName,
            values: modifier.values,
          }
        })
        .filter((o): o is IProductModifier => o !== null) || []
    )
  }
}

export enum CartItemType {
  VirtualGiftCardItem = 'VirtualGiftCardItem',
  PhysicalGiftCardItem = 'PhysicalGiftCardItem',
  ProductItem = 'ProductItem',
}

export enum RecipientOptions {
  SOMEONE_ELSE = 'Someone else',
  MYSELF = 'Myself',
}

export enum ScheduleOptions {
  SEND_IMMEDIATELY = 'Send immediately',
  SCHEDULE_LATER = 'Schedule for a later date',
}

export enum IProductFormStateKeys {
  recipientExpanded = 'recipientExpanded',
  scheduleExpanded = 'scheduleExpanded',
}

export enum HelpTextLabel {
  SERVICE_FEE_DESCRIPTION = 'Expand service fee description',
  DELIVERY_DATE = 'Delivery Date Help Text',
}

export type IProductFormState = { [key in IProductFormStateKeys]: boolean }
